// Serwis sprzętu - zgłoszenia RMA (`/api/rma`, PLAN-ZROWNANIA etap 6, SPIS RM-1..RM-9): stan poza Reactem jak plany -
// lista i otwarte zgłoszenie, wczytywane przy wejściu na ekran i po zdarzeniu SSE `konto` (`rma.zmiana` - wiadomość,
// wycena albo status od serwisu, RM-2). Statusy, nazwy i to, co wolno, liczy worker.
import { useSyncExternalStore } from "react";
import type {
  AccountEvent, RMAAddress, RMACreateIn, RMADevicesReply, RMAChanged, RMAFileIn, RMAListReply, RMAReply, RMATicketOut,
} from "../gen/kontrakt";
import { plik, zapytanie } from "./zaplecze";

interface Stan {
  wczytane: boolean;
  lista: readonly RMATicketOut[];
  otwarte: RMAReply | null;
}

let stan: Stan = { wczytane: false, lista: [], otwarte: null };
const sluchacze = new Set<() => void>();

function ustaw(s: Stan): void {
  stan = s;
  for (const f of sluchacze) f();
}

function zapisz(o: RMAReply): RMAReply {
  const id = o.zgloszenie.id;
  const jest = stan.lista.some((x) => x.id === id);
  ustaw({ ...stan, otwarte: o,
    lista: jest ? stan.lista.map((x) => (x.id === id ? o.zgloszenie : x)) : [o.zgloszenie, ...stan.lista] });
  return o;
}

export function useRMA(): Stan {
  return useSyncExternalStore((f) => { sluchacze.add(f); return () => sluchacze.delete(f); }, () => stan);
}

/** Lista zgłoszeń konta; błąd - zostaje poprzednia. */
export async function wczytajZgloszenia(): Promise<void> {
  const o = await zapytanie<RMAListReply>("GET", "/api/rma");
  ustaw({ ...stan, wczytane: true, lista: o.zgloszenia });
}

/** Szczegóły (otwarcie = przeczytane). */
export const wczytajZgloszenie = async (id: number) => zapisz(await zapytanie<RMAReply>("GET", "/api/rma/" + id));

/** Zamknięcie szczegółów - zdarzenia nie pobierają już zgłoszenia. */
export function zamknijZgloszenie(): void {
  if (stan.otwarte) ustaw({ ...stan, otwarte: null });
}

/** Zdarzenie SSE `konto`: zgłoszenie zmienione - lista i otwarte zgłoszenie od nowa. */
export function przyjmijZdarzenieRMA(e: AccountEvent): void {
  if (e.rodzaj !== "rma.zmiana") return;
  const z = e.dane as RMAChanged;
  if (stan.wczytane) void wczytajZgloszenia().catch(() => undefined);
  if (stan.otwarte?.zgloszenie.id === z.id) void wczytajZgloszenie(z.id).catch(() => undefined);
}

/** Wylogowanie: bez zgłoszeń poprzedniego konta. */
export function wyczyscRMA(): void {
  ustaw({ wczytane: false, lista: [], otwarte: null });
}

export const urzadzeniaRMA = () => zapytanie<RMADevicesReply>("GET", "/api/rma/urzadzenia");

export const noweZgloszenie = async (z: RMACreateIn) => zapisz(await zapytanie<RMAReply>("POST", "/api/rma", z));

const akcja = async (id: number, co: string, tresc: unknown) =>
  zapisz(await zapytanie<RMAReply>("POST", `/api/rma/${id}/${co}`, tresc));

export const decyzjaWyceny = (id: number, akceptuje: boolean) => akcja(id, "wycena", { akceptuje });
export const adresZwrotny = (id: number, a: RMAAddress) => akcja(id, "adres", a);
export const numerPrzesylki = (id: number, numer: string) => akcja(id, "przesylka", { numer });
export const wiadomosc = (id: number, tresc: string) => akcja(id, "wiadomosci", { tresc });
export const ocena = (id: number, o: number, komentarz: string) => akcja(id, "ocena", { ocena: o, komentarz });
/** Załącznik (base64 z uklad/zdjecie.ts). */
/** Załącznik zgłoszenia; z `wiadomosc` (może być pusta) - załącznik nowej wiadomości rozmowy (#61). */
export const zalacznik = (id: number, nazwa: string, b64: string, wiadomosc?: string) =>
  akcja(id, "pliki", { nazwa, plik: b64, w_wiadomosci: wiadomosc !== undefined, tekst: wiadomosc ?? "" } satisfies RMAFileIn);

/** Załącznik albo karta PDF jako bajty (sesja w ciasteczku). */
export const plikZgloszenia = (id: number, plikId: number) => plik(`/api/rma/${id}/pliki/${plikId}`);
export const kartaDoPaczki = (id: number) => plik(`/api/rma/${id}/pdf`);
